import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useScreenSize, useTouchCapability } from '../reports/MobileReportInteractions';

interface NavLinkProps {
  to: string;
  label: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  isActive?: boolean;
}

/**
 * A navigation link component that adapts to mobile/desktop views
 */
const NavLink: React.FC<NavLinkProps> = ({ to, label, icon, onClick, isActive = false }) => {
  const { isMobile, isPortrait } = useScreenSize();
  const isTouchDevice = useTouchCapability();
  
  // Use larger touch targets on mobile
  const touchClass = isTouchDevice 
    ? 'min-h-[48px] min-w-[48px]' 
    : '';
  
  // Desktop/tablet view
  if (!isMobile || isPortrait) {
    return (
      <Link 
        to={to} 
        className={`px-3 py-2 rounded transition-colors ${touchClass} ${
          isActive ? 'bg-blue-700' : 'hover:bg-blue-800'
        }`}
        onClick={onClick}
      >
        {label}
      </Link>
    );
  }
  
  // Mobile landscape view - icon-only with tooltip
  return (
    <Link
      to={to}
      aria-label={label}
      className={`flex flex-col items-center justify-center p-2 ${
        isActive ? 'text-blue-300' : 'text-white'
      }`}
      onClick={onClick}
    >
      <div className={`rounded-full p-2 ${isActive ? 'bg-blue-700' : 'bg-transparent'}`}>
        {icon || (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        )}
      </div>
      <span className="text-xs mt-1">{label}</span>
    </Link>
  );
};

/**
 * Responsive navbar component with landscape mode bottom navigation
 */
const Navbar: React.FC = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  
  // Get screen size and orientation information
  const { isMobile, isPortrait, width, height } = useScreenSize();
  const isTouchDevice = useTouchCapability();

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  // Close mobile menu
  const closeMenu = () => {
    setMenuOpen(false);
  };

  // Helper function to determine if a link is active
  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };
  
  // Listen for orientation changes to auto-close menu in landscape
  useEffect(() => {
    if (isMobile && !isPortrait) {
      // Auto-close dropdown menu when rotating to landscape
      setMenuOpen(false);
    }
  }, [isMobile, isPortrait]);
  
  // Render bottom navigation for mobile landscape mode
  const renderBottomNavigation = () => {
    if (!isMobile || isPortrait || !isAuthenticated) return null;
    
    return (
      <div className="fixed bottom-0 left-0 right-0 bg-blue-900 text-white px-2 py-1 flex justify-around z-20">
        <NavLink 
          to="/dashboard" 
          label="Dashboard"
          isActive={isActive('/dashboard')}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          }
        />
        
        <NavLink 
          to="/reports" 
          label="Reports"
          isActive={isActive('/reports')}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          }
        />
        
        <NavLink 
          to="/upload-enhanced" 
          label="Upload"
          isActive={isActive('/upload')}
          icon={
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
          }
        />
        
        {user?.role === 'admin' && (
          <NavLink 
            to="/admin" 
            label="Admin"
            isActive={isActive('/admin')}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            }
          />
        )}
        
        <button
          onClick={handleLogout}
          className="flex flex-col items-center justify-center p-2"
          aria-label="Logout"
        >
          <div className="rounded-full p-2">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </div>
          <span className="text-xs mt-1">Logout</span>
        </button>
      </div>
    );
  };

  // Apply padding to compensate for bottom navigation in landscape mode
  const containerPaddingClass = (!isPortrait && isMobile && isAuthenticated) 
    ? 'pb-16' // Add padding for the bottom navigation
    : '';

  return (
    <>
      {/* Main navigation bar */}
      <nav className={`bg-blue-900 text-white p-4 ${containerPaddingClass}`}>
        <div className="container mx-auto flex flex-wrap justify-between items-center">
          <div className="flex items-center">
            <Link to="/" className="text-2xl font-bold">
              {width < 640 ? 'ROI Marketing' : 'ROI Exteriors Marketing Portal'}
            </Link>
          </div>
          
          {/* Mobile menu button - only in portrait mode */}
          {(isMobile && isPortrait) && (
            <div className="md:hidden">
              <button 
                onClick={() => setMenuOpen(!menuOpen)}
                className="p-2 focus:outline-none min-h-[48px] min-w-[48px]"
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {menuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          )}
          
          {/* Desktop/Tablet menu */}
          {(!isMobile || (isMobile && isPortrait)) && isAuthenticated ? (
            <div className="hidden md:flex items-center space-x-4">
              <div className="text-gray-300 mr-4">
                <span>Logged in as: </span>
                <span className="font-semibold">{user?.firstName} {user?.lastName}</span>
                {user?.role === 'admin' && (
                  <span className="ml-2 bg-blue-700 text-xs px-2 py-1 rounded">Admin</span>
                )}
              </div>
              
              <div className="flex space-x-2">
                <Link 
                  to="/dashboard" 
                  className={`px-3 py-2 rounded transition-colors ${
                    isActive('/dashboard') ? 'bg-blue-700' : 'hover:bg-blue-800'
                  }`}
                >
                  Dashboard
                </Link>
                
                <div className="relative group">
                  <button 
                    className={`px-3 py-2 rounded transition-colors flex items-center ${
                      isActive('/reports') || isActive('/january-report') || isActive('/february-report') ||
                      isActive('/march-report') || isActive('/april-report') || isActive('/may-report') ||
                      isActive('/june-report') || isActive('/july-report') || isActive('/cc-') ||
                      isActive('/q1-report') || isActive('/q2-report') || isActive('/q3-report')
                        ? 'bg-blue-700' : 'hover:bg-blue-800'
                    }`}
                  >
                    Reports
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div className="absolute hidden group-hover:block bg-blue-800 rounded-md shadow-lg mt-1 py-2 w-64 z-10 max-h-[80vh] overflow-y-auto">
                    <div className="px-4 py-2 text-xs font-semibold text-gray-300 uppercase tracking-wider">
                      2026 Standard Reports
                    </div>
                    <Link
                      to="/january-2026-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      January 2026
                    </Link>
                    <Link
                      to="/february-2026-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      February 2026
                    </Link>
                    <Link
                      to="/march-2026-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      March 2026
                    </Link>
                    <Link
                      to="/april-2026-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      April 2026
                    </Link>
                    <Link
                      to="/may-2026-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      May 2026
                    </Link>
                    <Link
                      to="/june-2026-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      June 2026
                    </Link>
                    <Link
                      to="/july-2026-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      July 2026
                    </Link>
                    <Link
                      to="/q1-2026-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      Q1 2026
                    </Link>
                    <Link
                      to="/q2-2026-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      Q2 2026
                    </Link>
                    <Link
                      to="/ytd-2026-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      YTD 2026
                    </Link>

                    <div className="border-t border-blue-700 my-2"></div>

                    <div className="px-4 py-2 text-xs font-semibold text-gray-300 uppercase tracking-wider">
                      2026 Cody Cline Reports
                    </div>
                    <Link
                      to="/cc-january-2026-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      CC-January 2026
                    </Link>
                    <Link
                      to="/cc-february-2026-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      CC-February 2026
                    </Link>
                    <Link
                      to="/cc-march-2026-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      CC-March 2026
                    </Link>
                    <Link
                      to="/cc-april-2026-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      CC-April 2026
                    </Link>
                    <Link
                      to="/cc-may-2026-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      CC-May 2026
                    </Link>
                    <Link
                      to="/cc-june-2026-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      CC-June 2026
                    </Link>
                    <Link
                      to="/cc-july-2026-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      CC-July 2026
                    </Link>
                    <Link
                      to="/cc-q1-2026-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      CC-Q1 2026
                    </Link>
                    <Link
                      to="/cc-q2-2026-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      CC-Q2 2026
                    </Link>
                    <Link
                      to="/cc-ytd-2026-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      CC-YTD 2026
                    </Link>

                    <div className="border-t border-blue-700 my-2"></div>

                    <div className="px-4 py-2 text-xs font-semibold text-gray-300 uppercase tracking-wider">
                      2025 Standard Reports
                    </div>
                    <Link 
                      to="/january-report" 
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      January 2025
                    </Link>
                    <Link 
                      to="/february-report" 
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      February 2025
                    </Link>
                    <Link 
                      to="/dashboard-march-report" 
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      March 2025
                    </Link>
                    <Link 
                      to="/april-report" 
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      April 2025
                    </Link>
                    <Link 
                      to="/may-report" 
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      May 2025
                    </Link>
                    <Link 
                      to="/june-report" 
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      June 2025
                    </Link>
                    <Link
                      to="/july-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      July 2025
                    </Link>
                    <Link
                      to="/august-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      August 2025
                    </Link>
                    <Link
                      to="/september-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      September 2025
                    </Link>
                    <Link
                      to="/october-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      October 2025
                    </Link>
                    <Link
                      to="/november-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      November 2025
                    </Link>
                    <Link
                      to="/december-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      December 2025
                    </Link>
                    <Link
                      to="/q1-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      Q1 2025
                    </Link>
                    <Link
                      to="/q2-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      Q2 2025
                    </Link>
                    <Link
                      to="/q3-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      Q3 2025
                    </Link>
                    <Link
                      to="/q4-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      Q4 2025
                    </Link>
                    <Link
                      to="/ytd-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      YTD 2025
                    </Link>

                    <div className="border-t border-blue-700 my-2"></div>

                    <div className="px-4 py-2 text-xs font-semibold text-gray-300 uppercase tracking-wider">
                      Cody Cline Reports
                    </div>
                    <Link 
                      to="/cc-january-report" 
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      CC-January
                    </Link>
                    <Link 
                      to="/cc-february-report" 
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      CC-February
                    </Link>
                    <Link 
                      to="/cc-march-report" 
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      CC-March
                    </Link>
                    <Link 
                      to="/cc-april-report" 
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      CC-April
                    </Link>
                    <Link 
                      to="/cc-may-report" 
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      CC-May
                    </Link>
                    <Link 
                      to="/cc-june-report" 
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      CC-June
                    </Link>
                    <Link
                      to="/cc-july-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      CC-July
                    </Link>
                    <Link
                      to="/cc-august-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      CC-August
                    </Link>
                    <Link
                      to="/cc-september-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      CC-September
                    </Link>
                    <Link
                      to="/cc-october-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      CC-October
                    </Link>
                    <Link
                      to="/cc-november-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      CC-November
                    </Link>
                    <Link
                      to="/cc-december-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      CC-December
                    </Link>
                    <Link
                      to="/cc-q1-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      CC-Q1 2025
                    </Link>
                    <Link
                      to="/cc-q2-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      CC-Q2 2025
                    </Link>
                    <Link
                      to="/cc-q3-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      CC-Q3 2025
                    </Link>
                    <Link
                      to="/cc-q4-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      CC-Q4 2025
                    </Link>
                    <Link
                      to="/cc-ytd-report"
                      className="block px-4 py-2 text-sm hover:bg-blue-700"
                    >
                      CC-YTD 2025
                    </Link>

                    <div className="border-t border-blue-700 my-2"></div>

                    <Link
                      to="/saved-reports"
                      className="block px-4 py-2 text-sm font-semibold hover:bg-blue-700 text-yellow-300"
                    >
                      Saved Reports (Generated)
                    </Link>
                    <Link
                      to="/reports"
                      className="block px-4 py-2 text-sm font-semibold hover:bg-blue-700"
                    >
                      View All Reports →
                    </Link>
                  </div>
                </div>
                
                <div className="relative group">
                  <button 
                    className={`px-3 py-2 rounded transition-colors flex items-center ${isTouchDevice ? 'min-h-[48px]' : ''} ${
                      isActive('/upload') ? 'bg-blue-700' : 'hover:bg-blue-800'
                    }`}
                  >
                    Upload Data
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div className="absolute hidden group-hover:block bg-blue-800 rounded-md shadow-lg mt-1 py-1 w-56 z-10">
                    <Link
                      to="/generate-report"
                      className={`block px-4 py-2 hover:bg-blue-700 font-medium ${isTouchDevice ? 'min-h-[48px]' : ''}`}
                    >
                      Generate Report
                    </Link>
                    <Link
                      to="/saved-reports"
                      className={`block px-4 py-2 hover:bg-blue-700 font-medium ${isTouchDevice ? 'min-h-[48px]' : ''}`}
                    >
                      Saved Reports
                    </Link>
                    <div className="border-t border-blue-700 my-1"></div>
                    <Link
                      to="/upload-enhanced"
                      className={`block px-4 py-2 hover:bg-blue-700 ${isTouchDevice ? 'min-h-[48px]' : ''}`}
                    >
                      Standard Upload
                    </Link>
                    <Link
                      to="/upload-streaming"
                      className={`block px-4 py-2 hover:bg-blue-700 ${isTouchDevice ? 'min-h-[48px]' : ''}`}
                    >
                      Large File Upload (Streaming)
                    </Link>
                  </div>
                </div>
                
                {user?.role === 'admin' && (
                  <Link 
                    to="/admin" 
                    className={`px-3 py-2 rounded transition-colors ${
                      isActive('/admin') ? 'bg-blue-700' : 'hover:bg-blue-800'
                    }`}
                  >
                    Admin
                  </Link>
                )}
                
                <button
                  onClick={handleLogout}
                  className={`px-3 py-2 rounded bg-blue-700 hover:bg-blue-800 transition-colors ${isTouchDevice ? 'min-h-[48px]' : ''}`}
                >
                  Logout
                </button>
              </div>
            </div>
          ) : (!isMobile || (isMobile && isPortrait)) && !isAuthenticated && (
            <div className="hidden md:flex space-x-2">
              <Link 
                to="/login" 
                className={`px-3 py-2 rounded transition-colors ${isTouchDevice ? 'min-h-[48px]' : ''} ${
                  isActive('/login') ? 'bg-blue-700' : 'hover:bg-blue-800'
                }`}
              >
                Login
              </Link>
              <Link 
                to="/register" 
                className={`px-3 py-2 rounded bg-blue-700 hover:bg-blue-800 transition-colors ${isTouchDevice ? 'min-h-[48px]' : ''}`}
              >
                Register
              </Link>
            </div>
          )}
          
          {/* Mobile menu - Portrait mode only */}
          {menuOpen && isMobile && isPortrait && (
            <div className="md:hidden w-full mt-4 space-y-2">
              {isAuthenticated ? (
                <>
                  <div className="text-gray-300 pb-2 border-b border-blue-800">
                    <div>Logged in as:</div>
                    <div className="font-semibold">{user?.firstName} {user?.lastName}</div>
                    {user?.role === 'admin' && (
                      <span className="mt-1 inline-block bg-blue-700 text-xs px-2 py-1 rounded">Admin</span>
                    )}
                  </div>
                  
                  <Link 
                    to="/dashboard" 
                    className={`block px-3 py-2 rounded transition-colors min-h-[48px] ${
                      isActive('/dashboard') ? 'bg-blue-700' : 'hover:bg-blue-800'
                    }`}
                    onClick={closeMenu}
                  >
                    Dashboard
                  </Link>
                  
                  <div className="space-y-1">
                    <span className="block px-3 py-2 text-sm font-semibold text-gray-300">Reports</span>

                    <div className="pl-4 space-y-1">
                      <span className="block text-xs text-gray-400 uppercase tracking-wider px-3 py-1">2026 Standard Reports</span>
                      <Link
                        to="/january-2026-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/january-2026-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        January 2026
                      </Link>
                      <Link
                        to="/february-2026-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/february-2026-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        February 2026
                      </Link>
                      <Link
                        to="/march-2026-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/march-2026-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        March 2026
                      </Link>
                      <Link
                        to="/april-2026-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/april-2026-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        April 2026
                      </Link>
                      <Link
                        to="/may-2026-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/may-2026-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        May 2026
                      </Link>
                      <Link
                        to="/june-2026-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/june-2026-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        June 2026
                      </Link>
                      <Link
                        to="/july-2026-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/july-2026-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        July 2026
                      </Link>
                      <Link
                        to="/q1-2026-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/q1-2026-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        Q1 2026
                      </Link>
                      <Link
                        to="/q2-2026-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/q2-2026-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        Q2 2026
                      </Link>
                      <Link
                        to="/ytd-2026-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/ytd-2026-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        YTD 2026
                      </Link>

                      <span className="block text-xs text-gray-400 uppercase tracking-wider px-3 py-1 mt-2">2026 Cody Cline Reports</span>
                      <Link
                        to="/cc-january-2026-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/cc-january-2026-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        CC-January 2026
                      </Link>
                      <Link
                        to="/cc-february-2026-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/cc-february-2026-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        CC-February 2026
                      </Link>
                      <Link
                        to="/cc-march-2026-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/cc-march-2026-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        CC-March 2026
                      </Link>
                      <Link
                        to="/cc-april-2026-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/cc-april-2026-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        CC-April 2026
                      </Link>
                      <Link
                        to="/cc-may-2026-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/cc-may-2026-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        CC-May 2026
                      </Link>
                      <Link
                        to="/cc-june-2026-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/cc-june-2026-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        CC-June 2026
                      </Link>
                      <Link
                        to="/cc-july-2026-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/cc-july-2026-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        CC-July 2026
                      </Link>
                      <Link
                        to="/cc-q1-2026-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/cc-q1-2026-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        CC-Q1 2026
                      </Link>
                      <Link
                        to="/cc-q2-2026-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/cc-q2-2026-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        CC-Q2 2026
                      </Link>
                      <Link
                        to="/cc-ytd-2026-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/cc-ytd-2026-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        CC-YTD 2026
                      </Link>

                      <div className="border-t border-blue-700 my-1"></div>
                      <span className="block text-xs text-gray-400 uppercase tracking-wider px-3 py-1">2025 Standard Reports</span>
                      <Link 
                        to="/january-report" 
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/january-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        January 2025
                      </Link>
                      <Link 
                        to="/february-report" 
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/february-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        February 2025
                      </Link>
                      <Link 
                        to="/dashboard-march-report" 
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/dashboard-march-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        March 2025
                      </Link>
                      <Link 
                        to="/april-report" 
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/april-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        April 2025
                      </Link>
                      <Link 
                        to="/may-report" 
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/may-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        May 2025
                      </Link>
                      <Link 
                        to="/june-report" 
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/june-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        June 2025
                      </Link>
                      <Link
                        to="/july-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/july-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        July 2025
                      </Link>
                      <Link
                        to="/august-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/august-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        August 2025
                      </Link>
                      <Link
                        to="/september-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/september-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        September 2025
                      </Link>
                      <Link
                        to="/october-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/october-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        October 2025
                      </Link>
                      <Link
                        to="/november-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/november-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        November 2025
                      </Link>
                      <Link
                        to="/december-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/december-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        December 2025
                      </Link>
                      <Link
                        to="/q1-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/q1-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        Q1 2025
                      </Link>
                      <Link
                        to="/q2-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/q2-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        Q2 2025
                      </Link>
                      <Link
                        to="/q3-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/q3-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        Q3 2025
                      </Link>
                      <Link
                        to="/q4-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/q4-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        Q4 2025
                      </Link>
                      <Link
                        to="/ytd-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/ytd-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        YTD 2025
                      </Link>

                      <span className="block text-xs text-gray-400 uppercase tracking-wider px-3 py-1 mt-2">Cody Cline Reports</span>
                      <Link 
                        to="/cc-january-report" 
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/cc-january-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        CC-January
                      </Link>
                      <Link 
                        to="/cc-february-report" 
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/cc-february-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        CC-February
                      </Link>
                      <Link 
                        to="/cc-march-report" 
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/cc-march-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        CC-March
                      </Link>
                      <Link 
                        to="/cc-april-report" 
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/cc-april-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        CC-April
                      </Link>
                      <Link 
                        to="/cc-may-report" 
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/cc-may-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        CC-May
                      </Link>
                      <Link 
                        to="/cc-june-report" 
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/cc-june-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        CC-June
                      </Link>
                      <Link
                        to="/cc-july-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/cc-july-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        CC-July
                      </Link>
                      <Link
                        to="/cc-august-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/cc-august-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        CC-August
                      </Link>
                      <Link
                        to="/cc-september-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/cc-september-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        CC-September
                      </Link>
                      <Link
                        to="/cc-october-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/cc-october-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        CC-October
                      </Link>
                      <Link
                        to="/cc-november-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/cc-november-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        CC-November
                      </Link>
                      <Link
                        to="/cc-december-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/cc-december-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        CC-December
                      </Link>
                      <Link
                        to="/cc-q1-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/cc-q1-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        CC-Q1 2025
                      </Link>
                      <Link
                        to="/cc-q2-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/cc-q2-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        CC-Q2 2025
                      </Link>
                      <Link
                        to="/cc-q3-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/cc-q3-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        CC-Q3 2025
                      </Link>
                      <Link
                        to="/cc-q4-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/cc-q4-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        CC-Q4 2025
                      </Link>
                      <Link
                        to="/cc-ytd-report"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm ${
                          isActive('/cc-ytd-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        CC-YTD 2025
                      </Link>

                      <div className="border-t border-blue-700 my-1"></div>
                      <Link
                        to="/saved-reports"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm font-semibold text-yellow-300 ${
                          isActive('/saved-reports') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        Saved Reports (Generated)
                      </Link>
                      <Link
                        to="/reports"
                        className={`block px-3 py-2 rounded transition-colors min-h-[48px] text-sm font-semibold ${
                          isActive('/reports') ? 'bg-blue-700' : 'hover:bg-blue-800'
                        }`}
                        onClick={closeMenu}
                      >
                        View All Reports →
                      </Link>
                    </div>
                  </div>
                  
                  <div className="space-y-1 border-l-4 border-blue-700 pl-2">
                    <span className="block text-sm text-gray-400">Upload Data:</span>
                    <Link
                      to="/generate-report"
                      className={`block px-3 py-2 rounded transition-colors min-h-[48px] font-medium ${
                        isActive('/generate-report') ? 'bg-blue-700' : 'hover:bg-blue-800'
                      }`}
                      onClick={closeMenu}
                    >
                      Generate Report
                    </Link>
                    <Link
                      to="/saved-reports"
                      className={`block px-3 py-2 rounded transition-colors min-h-[48px] font-medium ${
                        isActive('/saved-reports') ? 'bg-blue-700' : 'hover:bg-blue-800'
                      }`}
                      onClick={closeMenu}
                    >
                      Saved Reports
                    </Link>
                    <Link
                      to="/upload-enhanced"
                      className={`block px-3 py-2 rounded transition-colors min-h-[48px] ${
                        isActive('/upload-enhanced') ? 'bg-blue-700' : 'hover:bg-blue-800'
                      }`}
                      onClick={closeMenu}
                    >
                      Standard Upload
                    </Link>
                    <Link 
                      to="/upload-streaming" 
                      className={`block px-3 py-2 rounded transition-colors min-h-[48px] ${
                        isActive('/upload-streaming') ? 'bg-blue-700' : 'hover:bg-blue-800'
                      }`}
                      onClick={closeMenu}
                    >
                      Large File Upload
                    </Link>
                  </div>
                  
                  {user?.role === 'admin' && (
                    <Link 
                      to="/admin" 
                      className={`block px-3 py-2 rounded transition-colors min-h-[48px] ${
                        isActive('/admin') ? 'bg-blue-700' : 'hover:bg-blue-800'
                      }`}
                      onClick={closeMenu}
                    >
                      Admin
                    </Link>
                  )}
                  
                  <button
                    onClick={() => {
                      handleLogout();
                      closeMenu();
                    }}
                    className="w-full text-left px-3 py-2 rounded bg-blue-700 hover:bg-blue-800 transition-colors min-h-[48px]"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link 
                    to="/login" 
                    className={`block px-3 py-2 rounded transition-colors min-h-[48px] ${
                      isActive('/login') ? 'bg-blue-700' : 'hover:bg-blue-800'
                    }`}
                    onClick={closeMenu}
                  >
                    Login
                  </Link>
                  <Link 
                    to="/register" 
                    className="block px-3 py-2 rounded bg-blue-700 hover:bg-blue-800 transition-colors min-h-[48px]"
                    onClick={closeMenu}
                  >
                    Register
                  </Link>
                </>
              )}
            </div>
          )}
        </div>
      </nav>
      
      {/* Render bottom navigation for landscape mode */}
      {renderBottomNavigation()}
      
      {/* Add body padding when in landscape mode with bottom nav */}
      {(!isPortrait && isMobile && isAuthenticated) && (
        <>
          {/* Apply padding to body for bottom navigation space */}
          <div className="pb-16" /> 
        </>
      )}
    </>
  );
};

export default Navbar;